if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = this.findProductElement(responseHTML);
          if (!this.productElement) {
            const productUrl = opener.getAttribute('data-product-url');
            if (productUrl) window.location.href = productUrl;
            return;
          }

          const originalSectionId = this.productElement.dataset.section;
          this.preventDuplicatedIDs();
          this.syncAbTestAttributes(originalSectionId);
          this.removeDOMElements();
          if (this.productElement.querySelector('media-gallery')) this.productElement.querySelector('media-gallery').dataset.disablePrepend = 'false';
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
          document.dispatchEvent(new CustomEvent('elanora:ab-content-updated', {
            detail: { root: this.modalContent }
          }));
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    syncAbTestAttributes(originalSectionId) {
      [
        'data-ab-test-section',
        'data-ab-test-id',
        'data-ab-current-product-handle',
        'data-canonical-product-handle',
        'data-canonical-path'
      ].forEach((attribute) => {
        this.modalContent.removeAttribute(attribute);
      });
      this.modalContent.removeAttribute('data-section');

      if (!this.productElement.hasAttribute('data-ab-test-section')) return;

      this.modalContent.setAttribute('data-ab-test-section', '');
      this.modalContent.setAttribute('data-section', `quickadd-${ originalSectionId }`);

      [
        'data-ab-test-id',
        'data-ab-current-product-handle',
        'data-canonical-product-handle',
        'data-canonical-path'
      ].forEach((attribute) => {
        const value = this.productElement.getAttribute(attribute);
        if (value) this.modalContent.setAttribute(attribute, value);
      });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    findProductElement(responseHTML) {
      const productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
      if (productElement) return productElement;

      const deferredProductHTML = this.getDeferredProductHTML(responseHTML);
      if (!deferredProductHTML) return null;

      return new DOMParser()
        .parseFromString(deferredProductHTML, 'text/html')
        .querySelector('section[id^="MainProduct-"]');
    }

    getDeferredProductHTML(responseHTML) {
      const scripts = responseHTML.querySelectorAll('script');

      for (const script of scripts) {
        const scriptText = script.textContent || '';
        if (!scriptText.includes('MainProduct-')) continue;

        const stringMatches = scriptText.match(/"(?:\\.|[^"\\])*"/g) || [];

        for (const stringMatch of stringMatches) {
          if (!stringMatch.includes('MainProduct-')) continue;

          try {
            const html = JSON.parse(stringMatch);
            if (typeof html === 'string' && html.includes('MainProduct-')) return html;
          } catch (error) {
            // Keep looking for the captured product section in other script strings.
          }
        }
      }

      return null;
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
